import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Button, TextField, Select } from '@mui/material';

// ============================================
// Modal Container
// ============================================
export const ModalContainer = styled(Box)({
  width: '620px',
  position: 'absolute',
  top: '50%',
  left: '50%',
  background: '#fff',
  borderRadius: '8px',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

// ============================================
// Header
// ============================================
export const ModalHeader = styled(Box)({
  height: '56px',
  background: '#262626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 32px',
});

export const ModalTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#fff',
});

export const CloseButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    color: '#fff',
    fontSize: '24px',
  },
});

// ============================================
// Content
// ============================================
export const ModalContent = styled(Box)({
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
});

export const FormSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

// ============================================
// Form Row
// ============================================
export const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  width: '100%',
});

export const FormLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#262626',
  width: '138px',
  flexShrink: 0,
});

export const FormInputWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// ============================================
// Date Picker
// ============================================
export const DatePickerWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8.5px',
  flex: 1,
});

export const DatePickerSeparator = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#000',
});

// ============================================
// Input Styles
// ============================================
export const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    height: '44px',
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#d9d9d9',
    },
    '&:hover fieldset': {
      borderColor: '#d9d9d9',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0F7F85',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
    fontWeight: 500,
    color: '#262626',
    padding: '12px 16px',
  },
  '& input::placeholder': {
    color: '#a6a6a6',
    opacity: 1,
  },
});

export const StyledSelect = styled(Select)({
  height: '44px',
  borderRadius: '6px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d9d9d9',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d9d9d9',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0F7F85',
  },
  '& .MuiSelect-select': {
    fontSize: '16px',
    fontWeight: 500,
    color: '#262626',
    padding: '12px 16px',
  },
});

// ============================================
// Small Input with Button
// ============================================
export const SmallInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '163px',
});

export const SmallTextField = styled(TextField)({
  width: '80px',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#d9d9d9',
    },
    '&:hover fieldset': {
      borderColor: '#d9d9d9',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0F7F85',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#262626',
    padding: '8px',
    textAlign: 'center',
  },
});

export const ChangeButton = styled(Button)({
  minWidth: 'auto',
  height: '32px',
  padding: '8px 16px',
  background: '#0F7F85',
  borderRadius: '4px',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
  '&:hover': {
    background: '#0a6a70',
  },
});

// ============================================
// Info Display
// ============================================
export const InfoDisplayWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '8px',
});

export const InfoDateText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isPlaceholder',
})(({ isPlaceholder }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: isPlaceholder ? '#a6a6a6' : '#262626',
}));

export const InfoDaysWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const InfoDaysNumber = styled(Typography)({
  fontSize: '20px',
  fontWeight: 500,
  color: '#0F7F85',
});

export const InfoDaysText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#0F7F85',
});

// ============================================
// Footer Buttons
// ============================================
export const ButtonWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
});

export const CancelButton = styled(Button)({
  width: '120px',
  height: '48px',
  padding: '12px 24px',
  background: '#d9d9d9',
  borderRadius: '8px',
  color: '#404040',
  fontSize: '20px',
  fontWeight: 600,
  '&:hover': {
    background: '#c0c0c0',
  },
});

export const ReleaseButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  width: '120px',
  height: '48px',
  padding: '12px 24px',
  background: isActive ? '#ef0c23' : '#d9d9d9',
  borderRadius: '8px',
  color: isActive ? '#fff' : '#a6a6a6',
  fontSize: '20px',
  fontWeight: 600,
  '&:hover': {
    background: isActive ? '#d00b1f' : '#d9d9d9',
  },
  '&.Mui-disabled': {
    background: '#d9d9d9',
    color: '#a6a6a6',
  },
}));

export const SaveButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  width: '240px',
  height: '48px',
  padding: '12px 24px',
  // --modal-primary(-hover) 는 상품 정보 수정 모달처럼 감싸는 쪽이 바꿀 때만 쓴다. 없으면 종전 색.
  background: isActive ? 'var(--modal-primary, #0F7F85)' : '#d9d9d9',
  borderRadius: '8px',
  color: isActive ? '#fff' : '#a6a6a6',
  fontSize: '20px',
  fontWeight: 600,
  '&:hover': {
    background: isActive ? 'var(--modal-primary-hover, #0a6a70)' : '#d9d9d9',
  },
  '&.Mui-disabled': {
    background: '#d9d9d9',
    color: '#a6a6a6',
  },
}));

// ============================================
// Period Modal Specific
// ============================================
export const PeriodFormLabel = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#262626',
  width: '85px',
  flexShrink: 0,
});

export const AutoApplyButton = styled(Button)({
  minWidth: 'auto',
  height: '36px',
  padding: '6px 12px',
  background: 'var(--modal-primary, #0F7F85)',
  borderRadius: '4px',
  color: '#fff',
  fontSize: '12px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:hover': {
    background: 'var(--modal-primary-hover, #0a6a70)',
  },
});

export const DisabledInputWrapper = styled(Box)({
  width: '163px',
  height: '44px',
  background: '#f2f2f2',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  '& .MuiTextField-root': {
    width: '100%',
  },
  '& .MuiOutlinedInput-root': {
    width: '100%',
    height: '44px',
    borderRadius: '8px',
    background: '#f2f2f2',
    '& fieldset': {
      borderColor: '#d9d9d9',
    },
    '&:hover fieldset': {
      borderColor: '#d9d9d9',
    },
    '&.Mui-disabled fieldset': {
      borderColor: '#d9d9d9',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
    fontWeight: 500,
    color: '#262626',
    padding: '12px 16px',
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: '#262626',
  },
});

export const PeriodInputWrapper = styled(Box)({
  width: '163px',
  height: '44px',
  display: 'flex',
  alignItems: 'center',
  '& .MuiTextField-root': {
    width: '100%',
  },
  '& .MuiOutlinedInput-root': {
    width: '100%',
    height: '44px',
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#d9d9d9',
    },
    '&:hover fieldset': {
      borderColor: '#d9d9d9',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0F7F85',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
    fontWeight: 500,
    color: '#262626',
    padding: '12px 16px',
  },
});

export const PeriodDatePickerWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});
